import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, IconButton, Input, Stack, Tooltip, Typography } from '@mui/joy';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import RedoRoundedIcon from '@mui/icons-material/RedoRounded';
import FinanceSidePanel, { SIDE_PANEL_HEADER } from '../finance/FinanceSidePanel';

const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#6366f1', '#ffffff', '#111827'];
const TOOLS = [['pen', 'Карандаш'], ['arrow', 'Стрелка'], ['rectangle', 'Рамка'], ['ellipse', 'Овал']];

function paintStroke(ctx, stroke) {
  const points = stroke.points;
  if (!points.length) return;
  const a = points[0], b = points[points.length - 1];
  ctx.strokeStyle = stroke.color; ctx.fillStyle = stroke.color; ctx.lineWidth = stroke.width;
  ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.beginPath();
  if (stroke.tool === 'rectangle') ctx.rect(a.x, a.y, b.x - a.x, b.y - a.y);
  else if (stroke.tool === 'ellipse') ctx.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, Math.abs(b.x - a.x) / 2, Math.abs(b.y - a.y) / 2, 0, 0, Math.PI * 2);
  else {
    ctx.moveTo(a.x, a.y);
    if (points.length === 1) ctx.lineTo(a.x + .1, a.y + .1);
    else for (const point of stroke.tool === 'arrow' ? [b] : points.slice(1)) ctx.lineTo(point.x, point.y);
  }
  ctx.stroke();
  if (stroke.tool === 'arrow') {
    const angle = Math.atan2(b.y - a.y, b.x - a.x), length = stroke.width * 5;
    ctx.beginPath(); ctx.moveTo(b.x, b.y);
    ctx.lineTo(b.x - length * Math.cos(angle - Math.PI / 6), b.y - length * Math.sin(angle - Math.PI / 6));
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(b.x - length * Math.cos(angle + Math.PI / 6), b.y - length * Math.sin(angle + Math.PI / 6));
    ctx.stroke();
  }
}

export default function ImageAnnotationEditor({ image, onClose, onApply }) {
  const canvasRef = useRef(null), bitmapRef = useRef(null), draftRef = useRef(null), frameRef = useRef(0);
  const [strokes, setStrokes] = useState(image.strokes || []), strokesRef = useRef(strokes);
  const [redo, setRedo] = useState([]), [tool, setTool] = useState('pen'), [color, setColor] = useState(COLORS[0]), [width, setWidth] = useState(4);
  const [ready, setReady] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState('');
  strokesRef.current = strokes;

  function paint() {
    const canvas = canvasRef.current, bitmap = bitmapRef.current;
    if (!canvas || !bitmap) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    for (const stroke of strokesRef.current) paintStroke(ctx, stroke);
    if (draftRef.current) paintStroke(ctx, draftRef.current);
  }
  function schedulePaint() {
    cancelAnimationFrame(frameRef.current); frameRef.current = requestAnimationFrame(paint);
  }
  useEffect(() => {
    let active = true;
    const bitmap = new Image();
    if (/^https?:/i.test(image.src)) bitmap.crossOrigin = 'anonymous';
    bitmap.onload = () => {
      if (!active || !canvasRef.current) return;
      const scale = Math.min(1, 4096 / Math.max(bitmap.naturalWidth, bitmap.naturalHeight));
      canvasRef.current.width = Math.max(1, Math.round(bitmap.naturalWidth * scale));
      canvasRef.current.height = Math.max(1, Math.round(bitmap.naturalHeight * scale));
      bitmapRef.current = bitmap; setReady(true); paint();
    };
    bitmap.onerror = () => { if (active) setError('Не удалось открыть изображение. Для фото из старого комментария можно скачать файл и прикрепить его заново.'); };
    bitmap.src = image.src;
    return () => { active = false; cancelAnimationFrame(frameRef.current); bitmap.onload = null; bitmap.onerror = null; };
    // The parent gives each edited attachment its own component key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { paint(); }, [strokes]); // eslint-disable-line react-hooks/exhaustive-deps

  function point(event) {
    const canvas = canvasRef.current, rect = canvas.getBoundingClientRect();
    return { x: Math.max(0, Math.min(canvas.width, (event.clientX - rect.left) * canvas.width / rect.width)), y: Math.max(0, Math.min(canvas.height, (event.clientY - rect.top) * canvas.height / rect.height)) };
  }
  function start(event) {
    if (!ready || busy || draftRef.current || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId);
    draftRef.current = { tool, color, width: width * Math.max(canvasRef.current.width, canvasRef.current.height) / 1000, points: [point(event)], pointerId: event.pointerId };
    schedulePaint();
  }
  function move(event) {
    const draft = draftRef.current;
    if (!draft || draft.pointerId !== event.pointerId) return;
    event.preventDefault();
    const next = point(event);
    draft.points = draft.tool === 'pen' ? [...draft.points, next] : [draft.points[0], next]; schedulePaint();
  }
  function finish(event) {
    if (!draftRef.current || draftRef.current.pointerId !== event.pointerId) return;
    move(event);
    const { pointerId, ...stroke } = draftRef.current; draftRef.current = null;
    setStrokes(prev => [...prev, stroke]); setRedo([]);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }
  function cancelStroke() { draftRef.current = null; schedulePaint(); }
  function undo() { if (strokes.length) { setRedo(prev => [...prev, strokes[strokes.length - 1]]); setStrokes(prev => prev.slice(0, -1)); } }
  function redoStroke() { if (redo.length) { setStrokes(prev => [...prev, redo[redo.length - 1]]); setRedo(prev => prev.slice(0, -1)); } }
  async function apply() {
    if (!ready || busy || draftRef.current) return;
    setBusy(true); setError('');
    try {
      paint();
      const blob = await new Promise((resolve, reject) => canvasRef.current.toBlob(result => result ? resolve(result) : reject(new Error('encode')), 'image/png'));
      if (blob.size > 25 * 1024 * 1024) throw new Error('size');
      const name = (image.name || 'photo').replace(/\.[^.]+$/, '').replace(/-marked$/, '') + '-marked.png';
      onApply(new File([blob], name, { type: 'image/png' }), strokes);
    } catch (err) {
      setError(err.message === 'size' ? 'Результат больше 25 МБ. Уменьшите исходное изображение.' : 'Не удалось сохранить разметку. Попробуйте прикрепить исходное фото с устройства.');
    } finally { setBusy(false); }
  }
  return <FinanceSidePanel label="Разметка изображения" width={780} onClose={onClose} busy={busy} closeLabel="Закрыть разметку">
    <Box sx={SIDE_PANEL_HEADER}><Typography level="title-md">Разметка изображения</Typography><Typography level="body-xs" noWrap>{image.name}</Typography></Box>
    <Stack spacing={1.5} sx={{ p: { xs: 1, sm: 2 }, minHeight: 0, overflow: 'auto', flex: 1 }}>
      <Stack direction="row" sx={{ gap: .5, flexWrap: 'wrap' }}>{TOOLS.map(([id, name]) => <Button key={id} size="sm" variant={tool === id ? 'solid' : 'soft'} disabled={!ready || busy} aria-pressed={tool === id} onClick={() => setTool(id)}>{name}</Button>)}</Stack>
      <Stack direction="row" alignItems="center" sx={{ gap: .7, flexWrap: 'wrap' }}>
        {COLORS.map(value => <IconButton key={value} size="sm" aria-label={`Цвет ${value}`} aria-pressed={color === value} disabled={busy} onClick={() => setColor(value)} sx={{ width: 28, height: 28, minWidth: 28, minHeight: 28, bgcolor: value, border: color === value ? '3px solid #94a3b8' : '1px solid #cbd5e1', '&:hover': { bgcolor: value, opacity: .8 } }} />)}
        <Input type="number" size="sm" value={width} disabled={busy} onChange={e => setWidth(Math.max(1, Math.min(16, Number(e.target.value) || 1)))} slotProps={{ input: { min: 1, max: 16, 'aria-label': 'Толщина линии' } }} sx={{ width: 70 }} />
        <Tooltip title="Отменить штрих"><IconButton size="sm" aria-label="Отменить штрих" disabled={!strokes.length || busy} onClick={undo}><UndoRoundedIcon /></IconButton></Tooltip>
        <Tooltip title="Вернуть штрих"><IconButton size="sm" aria-label="Вернуть штрих" disabled={!redo.length || busy} onClick={redoStroke}><RedoRoundedIcon /></IconButton></Tooltip>
        <Button size="sm" variant="plain" color="neutral" disabled={!strokes.length || busy} onClick={() => { setStrokes([]); setRedo([]); }}>Очистить</Button>
      </Stack>
      {!ready && !error && <Typography level="body-sm">Открываю изображение…</Typography>}
      {error && <Alert color="danger" size="sm">{error}</Alert>}
      <Box sx={{ bgcolor: 'neutral.softBg', p: .5, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 80 }}>
        <canvas ref={canvasRef} aria-label="Холст разметки" onPointerDown={start} onPointerMove={move} onPointerUp={finish} onPointerCancel={cancelStroke} onLostPointerCapture={cancelStroke} style={{ display: ready ? 'block' : 'none', width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '55dvh', touchAction: 'none', cursor: 'crosshair' }} />
      </Box>
      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Рисуйте мышью или пальцем. Изображение с разметкой будет прикреплено после нажатия «Применить». Большие фото уменьшаются до 4096 px по длинной стороне.</Typography>
      <Stack direction="row" justifyContent="flex-end" spacing={1}><Button size="sm" variant="plain" color="neutral" disabled={busy} onClick={onClose}>Отмена</Button><Button size="sm" disabled={!ready || busy} loading={busy} onClick={apply}>Применить</Button></Stack>
    </Stack>
  </FinanceSidePanel>;
}
